import { BUILDER_ID, BuilderStepId } from '../data/builder'
import { requestOffer, scrollToPackCta } from './offerBus'

/**
 * Short, ad-friendly anchors. Each one opens the page at its section with the
 * right option already selected:
 *   /#build    → Build your X1, Step 1 open
 *   /#complete → single Complete System card, selected
 *   /#trio     → Buy 2 Get 1 Free card, selected
 *   /#crew     → Crew Pack (30 systems)
 *   /#rx       → Prescription section
 */
export type DeepLink = 'build' | 'complete' | 'trio' | 'crew' | 'rx'

export const DEEP_LINKS: DeepLink[] = ['build', 'complete', 'trio', 'crew', 'rx']

export function isDeepLink(value: string): value is DeepLink {
  return (DEEP_LINKS as string[]).includes(value)
}

const BUILDER_STEP_EVENT = 'gritvue:builder-step'

/** Opens a builder step from anywhere on the page (the builder also flashes its frame). */
export function requestBuilderStep(step: BuilderStepId) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<BuilderStepId>(BUILDER_STEP_EVENT, { detail: step }))
}

export function onBuilderStepRequest(handler: (step: BuilderStepId) => void) {
  const listener = (event: Event) => handler((event as CustomEvent<BuilderStepId>).detail)
  window.addEventListener(BUILDER_STEP_EVENT, listener)
  return () => window.removeEventListener(BUILDER_STEP_EVENT, listener)
}

/** Waits for a late-mounted section, then scrolls it under the sticky header. */
function scrollToId(id: string) {
  const attempt = (tries: number) => {
    const target = document.getElementById(id)
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    if (tries < 25) window.setTimeout(() => attempt(tries + 1), 80)
  }
  window.setTimeout(() => attempt(0), 60)
}

/** Keeps the address bar shareable (#trio etc.) without triggering a second jump. */
function setHash(link: DeepLink) {
  if (window.location.hash === `#${link}`) return
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${link}`)
}

export function goToDeepLink(link: DeepLink, options: { builderStep?: BuilderStepId; updateHash?: boolean } = {}) {
  if (typeof window === 'undefined') return
  if (options.updateHash !== false) setHash(link)
  switch (link) {
    case 'build':
      requestBuilderStep(options.builderStep ?? 'lens')
      scrollToId(BUILDER_ID)
      return
    case 'complete':
      requestOffer('single')
      return
    case 'trio':
      requestOffer('trio')
      return
    case 'crew':
      scrollToPackCta('business')
      return
    case 'rx':
      scrollToId('rx')
      return
  }
}

/** Click handler for <a href="#build"> style links: smooth, preselecting, no page jump. */
export function deepLinkClick(link: DeepLink, options?: { builderStep?: BuilderStepId }) {
  return (event: { preventDefault: () => void }) => {
    event.preventDefault()
    goToDeepLink(link, options)
  }
}
